import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Download, Mail, Sparkles, Trash2 } from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { AdminModal } from "@/components/AdminModal";
import { Header } from "./Schools";

export default function Certificates() {
  const [items, setItems] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ teacher_name: "", school_name: "", training_name: "", date: "", email: "", training_hours: "", training_duration: "", program_id: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ training_name: "", date: "", school_name: "", teachers_csv: "" });

  const load = () => api.get("/certificates").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/programs").then((r) => setPrograms(r.data || []));
  }, []);

  // When user picks a program from the dropdown, auto-fill training name + duration
  const onProgramSelect = (programId) => {
    const p = programs.find((x) => x.id === programId);
    if (!p) return;
    const dur = p.duration_hours ? `${p.duration_hours} hours` : "";
    setF((prev) => ({ ...prev, program_id: p.id, training_name: p.title, training_hours: dur, training_duration: dur }));
  };

  const remove = async (certId) => {
    if (!window.confirm("Delete this certificate? It will no longer verify publicly. This cannot be undone.")) return;
    try {
      await api.delete(`/certificates/${certId}`);
      toast.success("Certificate deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const create = async () => {
    try {
      await api.post("/certificates", f);
      toast.success("Certificate created");
      setShow(false);
      setF({ teacher_name: "", school_name: "", training_name: "", date: "", email: "", training_hours: "", training_duration: "", program_id: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const runBulk = async () => {
    const rows = bulk.teachers_csv.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const teachers = rows.map((line) => {
      const [name, email] = line.split(",").map((s) => s.trim());
      return { name, email };
    });
    try {
      const { data } = await api.post("/certificates/bulk", { ...bulk, teachers });
      toast.success(`${data.count} certificates generated`); setBulkOpen(false); setBulk({ training_name: "", date: "", school_name: "", teachers_csv: "" }); load();
    } catch { toast.error("Failed"); }
  };

  const emailCert = async (cid) => {
    try { await api.post(`/certificates/${cid}/email`); toast.success("Email queued"); load(); }
    catch { toast.error("Email failed (add RESEND_API_KEY)"); }
  };

  return (
    <div className="fade-up">
      <Header title="Certificates" subtitle="Generate, download, email, and verify." actions={
        <div className="flex gap-2">
          <button onClick={() => setBulkOpen(true)} data-testid="bulk-cert" className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-[#0F172A] text-sm font-medium px-4 py-2 rounded-lg"><Sparkles className="w-4 h-4" /> Bulk generate</button>
          <button onClick={() => setShow(true)} data-testid="add-cert" className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg"><Plus className="w-4 h-4" /> New certificate</button>
        </div>
      } />

      <div className="mt-6 rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr><th className="text-left px-5 py-3 font-medium">Teacher</th><th className="text-left px-5 py-3 font-medium">School</th><th className="text-left px-5 py-3 font-medium">Training</th><th className="text-left px-5 py-3 font-medium">Duration</th><th className="text-left px-5 py-3 font-medium">Date</th><th className="text-left px-5 py-3 font-medium">ID</th><th className="text-right px-5 py-3 font-medium">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-5 py-3 font-medium">
                  {c.teacher_name}
                  {c.certificate_type === "podcast" && (
                    <span className="ml-2 inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded-full align-middle">Podcast</span>
                  )}
                </td>
                <td className="px-5 py-3 text-gray-600">{c.school_name}</td>
                <td className="px-5 py-3">{c.training_name}</td>
                <td className="px-5 py-3 text-gray-600">{c.training_duration || c.training_hours || "—"}</td>
                <td className="px-5 py-3">{c.date}</td>
                <td className="px-5 py-3 font-mono text-xs text-gray-500">{c.certificate_id}</td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <a href={`${BACKEND}/api/certificates/${c.certificate_id}/pdf`} target="_blank" rel="noreferrer" data-testid={`download-${c.certificate_id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100"><Download className="w-3 h-3" /> PDF</a>
                    {c.email && <button onClick={() => emailCert(c.certificate_id)} data-testid={`email-${c.certificate_id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100"><Mail className="w-3 h-3" /> {c.emailed ? "Resend" : "Email"}</button>}
                    <button onClick={() => remove(c.certificate_id)} data-testid={`delete-${c.certificate_id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-rose-100 hover:text-rose-700"><Trash2 className="w-3 h-3" /> Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-gray-500">No certificates yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {show && (
        <AdminModal onClose={() => setShow(false)} title="New certificate" testId="cert-modal" footer={
          <button onClick={create} data-testid="cert-save" className="h-11 px-5 rounded-lg bg-[#0F172A] text-white text-sm font-medium w-full sm:w-auto">Generate</button>
        }>
          <div className="grid gap-2">
            {/* Program selector — auto-fills Training Name and Duration */}
            {programs.length > 0 && (
              <div>
                <label className="text-[11px] text-gray-500 font-medium block mb-1">Auto-fill from Program (optional)</label>
                <select
                  onChange={(e) => onProgramSelect(e.target.value)}
                  defaultValue=""
                  className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full"
                  data-testid="cert-program-select"
                >
                  <option value="">— Select a program —</option>
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>{p.title} ({p.duration_hours}h)</option>
                  ))}
                </select>
              </div>
            )}
            <input placeholder="Teacher name" value={f.teacher_name} onChange={(e) => setF({ ...f, teacher_name: e.target.value })} data-testid="cert-teacher_name" className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm" />
            <input placeholder="School name" value={f.school_name} onChange={(e) => setF({ ...f, school_name: e.target.value })} data-testid="cert-school_name" className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm" />
            <input placeholder="Training name" value={f.training_name} onChange={(e) => setF({ ...f, training_name: e.target.value })} data-testid="cert-training_name" className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm" />
            <input placeholder="Date (e.g. 24 Jul 2026)" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="cert-date" className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm" />
            <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="cert-email" className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm" />
            {/* Duration — auto-filled from program selector; editable as override */}
            <div>
              <input
                placeholder="Duration (e.g. 8 hours) — auto-filled from program"
                value={f.training_duration || f.training_hours || ""}
                onChange={(e) => setF({ ...f, training_hours: e.target.value, training_duration: e.target.value })}
                data-testid="cert-training_duration"
                className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full"
              />
              <p className="text-[11px] text-gray-400 mt-1">Auto-filled when you pick a program above.</p>
            </div>
          </div>
        </AdminModal>
      )}

      {bulkOpen && (
        <AdminModal onClose={() => setBulkOpen(false)} title="Bulk generate" testId="bulk-modal" footer={
          <button onClick={runBulk} data-testid="bulk-generate" className="h-11 px-5 rounded-lg bg-[#4F46E5] text-white text-sm font-medium w-full sm:w-auto">Generate certificates</button>
        }>
          <div className="grid gap-2">
            <input placeholder="School name" value={bulk.school_name} onChange={(e) => setBulk({ ...bulk, school_name: e.target.value })} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm" data-testid="bulk-school" />
            <input placeholder="Training name" value={bulk.training_name} onChange={(e) => setBulk({ ...bulk, training_name: e.target.value })} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm" data-testid="bulk-training" />
            <input placeholder="Date (e.g. 12 Oct 2025)" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm" data-testid="bulk-date" />
            <textarea placeholder="Teachers (one per line: name, email)" value={bulk.teachers_csv} onChange={(e) => setBulk({ ...bulk, teachers_csv: e.target.value })} className="px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm min-h-40 font-mono" data-testid="bulk-teachers" />
          </div>
        </AdminModal>
      )}
    </div>
  );
}
